import React, { Component, Context } from "react";
import { Text, View, FlatList, SafeAreaView, ScrollView, KeyboardType, ListRenderItemInfo } from "react-native";
import {
	IForm,
	ISection,
	IFormField,
	ISelectFormField,
	IFormInputProps,
	IFormInputCustomProps,
	FormListItem,
} from "../../base/models/forms";
import FormInput from "../../components/FormInput";
import FormPicker from "../../components/FormPicker";
import styles from "../../styles";
import { FormValidation, FormField, CustomValidator } from "../../base/form-validation";
import { dynamicSize, getFontSize, dynamicSizeIncludingDesktop } from "../../helpers";
import PMButton from "../../components/PMButton";
import CheckboxList from "../../components/CheckboxList";
import theme from "../../theme";
import DescriptionText from "../../components/DescriptionText";
import NotificationsService from "../../services/notifications";
import LoadingOverlay from "../../components/LoadingOverlay";
import withFirebaseUtils, { IFirebaseUtils } from "../withFirebaseUtils";
import { IUserAuthPayload } from "../../base/models/user";
import { IPlatformContext, PlatformContext } from "../../platform/context";
import { getKeyboardType } from "../../base/render-utils/form";
import { DynamicFormValidation, DynamicFormField } from "../../base/form-validation/dynamic";
import { IFormSubmission } from "../../base/models/formSubmission";

export type SubmitFormProps = {
	formId: string;
	form?: IForm;
	values?: { [key: string]: any };
	disableFields?: { [key: string]: boolean };
	hideTitle?: boolean;
	onFormSubmitted?: () => any;
	onSubmitOverride?: (
		formId: string,
		values,
		formConfig: IForm,
		userPayload: Partial<IUserAuthPayload>,
		originalValues
	) => Promise<any>;
	submitBoth?: boolean;
	userPayload?: IUserAuthPayload;
	addContactFields?: boolean;
	addContactFieldsNameOnly?: boolean;
	withAsterisks?: boolean;
	asteriskTemplate?: string | React.ReactNode;
	customProps?: IFormInputCustomProps;
	customValidators?: { [key: string]: CustomValidator | CustomValidator[] };
	btnText?: string;
	successMsg?: string;
	footerBtnContent?: React.ReactNode;
};

type SubmitFormState = {
	formId: string;
	formConfig: IForm;
	formData: FormValidation;
	loading: boolean;
	submitting: boolean;
	refresh: boolean;
};

export function getFieldKey(field: IFormField): string {
	if (!field.section) return field.id;
	return `${field.section}-${field.id}`;
}

const getFieldValueByRawKey = (rawKey: string, formConfig: IForm, form: FormValidation): any => {
	const field: IFormField = formConfig.fieldsMap[rawKey];
	if (!field) return "";
	const key: string = getFieldKey(field);
	return form.fields[key]?.value;
};

class SubmitForm extends Component<SubmitFormProps & IFirebaseUtils, SubmitFormState> {
	static contextType: Context<IPlatformContext> = PlatformContext;

	state = {
		formId: this.props.formId,
		formConfig: null,
		formData: null,
		loading: true,
		submitting: false,
		refresh: false, // FlatList is annoying to refresh
	};

	async componentDidMount() {
		try {
			await this.init();
		} catch (err) {
			console.log(err);
			this.setState({ loading: false });
		}
	}

	async init() {
		const formId: string = this.props.formId;
		console.log("formId:", formId);
		if (!formId && !this.props.form) return;
		const formConfig: IForm = this.props.form || (await this.props.getForm(this.props.formId));
		if (this.props.addContactFields && !formConfig.omitContactFields) {
			const nameField: IFormField = {
				id: "user-name",
				label: "Name",
				fieldType: "text",
				order: 1,
				placeholder: "Enter your name",
				defaultValue: this.props.userPayload?.displayName,
			};
			const emailField: IFormField = {
				id: "user-email",
				label: "Email",
				fieldType: "email",
				order: 2,
				placeholder: "Enter your email",
				defaultValue: this.props.userPayload?.email,
			};
			formConfig.fieldsMap["user-name"] = { ...nameField, ...(formConfig.fieldsMap["user-name"] || {}) };
			if (!this.props.addContactFieldsNameOnly) {
				formConfig.fieldsMap["user-email"] = { ...emailField, ...(formConfig.fieldsMap["user-email"] || {}) };
				if (!formConfig.fields.find((f: IFormField) => f.id === "user-email"))
					formConfig.fields.unshift(formConfig.fieldsMap["user-email"]);
			}
			if (!formConfig.fields.find((f: IFormField) => f.id === "user-name"))
				formConfig.fields.unshift(formConfig.fieldsMap["user-name"]);
		}
		const fields: { [index: string]: FormField } = {};
		for (let fieldId in formConfig.fieldsMap) {
			const formField: IFormField = formConfig.fieldsMap[fieldId];
			const key: string = getFieldKey(formField);
			let value: any = this.props.values
				? this.props.values[key] || formField.defaultValue || ""
				: formField.defaultValue || "";
			if (formField.fromUserData && this.props.userPayload && this.props.userPayload.profile) {
				if (formField.userDataField && formField.userDataField.indexOf(",") > 0) {
					const keys: string[] = formField.userDataField.split(",");
					if (keys.length) {
						const values: string[] = [];
						keys.forEach((k) => {
							const formattedKey: string = (k || "").trim();
							const val: string = this.props.userPayload.profile[formattedKey];
							if (val) values.push(val);
						});
						value = values.length ? values.join(" ") : this.props.userPayload.profile[key] || value;
					}
				} else {
					value = this.props.userPayload.profile[formField.userDataField || key] || value;
				}
			}
			fields[key] = {
				...formField,
				value,
				key,
				mandatory: !formField.optional,
				numeric: formField.fieldType === "number",
				email: formField.fieldType === "email",
				isDate: formField.fieldType === "date" || formField.fieldType === "datetime",
				minLength: formField.minLength,
				maxLength: formField.maxLength,
				customValidator: this.props.customValidators ? this.props.customValidators[key] : undefined,
			};
			if (formField.fields) {
				const nestedFields: { [key: string]: DynamicFormField & any } = {};
				let order: number = 0;
				for (let nestedKey in formField.fields) {
					const nestedFormField: IFormField = formField.fields[nestedKey];
					nestedFields[nestedKey] = {
						...nestedFormField,
						value: "",
						key: nestedKey,
						mandatory: !nestedFormField.optional,
						numeric: nestedFormField.fieldType === "number",
						email: nestedFormField.fieldType === "email",
						isDate: nestedFormField.fieldType === "date" || nestedFormField.fieldType === "datetime",
						minLength: nestedFormField.minLength,
						maxLength: nestedFormField.maxLength,
						order,
					};
					order++;
				}
				const nestedForm: DynamicFormValidation = new DynamicFormValidation(nestedFields);
				fields[key].form = nestedForm;
			}
		}
		const formData: FormValidation = new FormValidation(fields);
		console.log("formData:", formData);
		console.log("formConfig:", formConfig);
		this.setState({
			formConfig,
			formData,
			loading: false,
			refresh: !this.state.refresh,
		});
		if (this.context.initStore && formConfig.ecwidSku) {
			this.context.initStore();
		}
	}

	onInputChange = (fieldConfig: IFormField, value: any) => {
		console.log("onInputChange:", fieldConfig, value);
		this.setState({
			formData: this.state.formData.formFieldOnBlur(getFieldKey(fieldConfig), value),
		});
		if (
			fieldConfig.fieldType !== "text" &&
			fieldConfig.fieldType !== "textarea" &&
			fieldConfig.fieldType !== "phone" &&
			fieldConfig.fieldType !== "email"
			// fieldConfig.fieldType !== "number"
		) {
			this.setState({ refresh: !this.state.refresh });
		}
	};

	onBlur = (fieldConfig: IFormField) => {
		console.log("onBlur:", fieldConfig);
		const key: string = getFieldKey(fieldConfig);
		this.setState({
			formData: this.state.formData.formFieldOnBlur(key, this.state.formData.fields[key].value),
			// refresh: !this.state.refresh, // dana
		});
	};

	async onSubmit(flowToBeContinued?: boolean): Promise<IFormSubmission | void> {
		// TODO: inline validation and error messages
		const form: FormValidation = this.state.formData.getValidatedForm();
		if (!form.isValid) {
			let formConfig: IForm = this.state.formConfig;
			formConfig.updateKey = Date.now();
			this.setState({
				formData: form,
				formConfig,
				refresh: !this.state.refresh,
			}); // update field validity
			NotificationsService.error("Please fix form errors.");
			console.log(form);
			return Promise.reject();
		}

		this.setState({ submitting: true });

		let userPayload: Partial<IUserAuthPayload> = this.props.userPayload;
		if (this.props.addContactFields) {
			const userEmail: string = getFieldValueByRawKey("user-email", this.state.formConfig, this.state.formData);
			const userName: string = getFieldValueByRawKey("user-name", this.state.formConfig, this.state.formData);
			userPayload = {
				uid: this.props.userPayload?.uid || "site-contact",
				email: userEmail,
				displayName: userName,
			};
		}
		const isOverride: boolean = !!this.props.onSubmitOverride;
		const onSubmit = this.props.onSubmitOverride || this.props.addFormSubmission;
		let values = this.state.formData.values;
		for (let key in values) {
			const value = values[key];
			if (value === undefined) {
				delete values[key];
			}
			if (Array.isArray(value) && value.length) {
				const items: FormListItem[] = value as FormListItem[];
				const formatted: string[] = [];
				items.forEach((item: FormListItem, i: number) => {
					if (item.checked) {
						let formattedVal: string = item.value as string;
						if (i === 0) formattedVal += ".";
						if (item.form) {
							item.form.getOrderedFields().forEach((f: DynamicFormField) => {
								formattedVal += ` ${f.label || f.placeholder}: ${f.value} `;
							});
						}
						formatted.push(formattedVal);
					}
				});
				values[key] = formatted;
			}
		}

		if (this.props.submitBoth && !!this.props.onSubmitOverride) {
			await this.props.addFormSubmission(this.props.formId, values, this.state.formConfig, userPayload);
		}

		return onSubmit(this.props.formId, values, this.state.formConfig, userPayload, this.state.formData.values)
			.then((formSubmissionDetails: IFormSubmission) => {
				if (!isOverride && !flowToBeContinued)
					NotificationsService.success(
						this.props.successMsg ||
							this.state.formConfig?.successMsg ||
							"Your request has been submitted.",
						undefined,
						{
							mobileCallback: this.props.onFormSubmitted,
						}
					);
				if (!flowToBeContinued) {
					this.setState({
						formData: this.state.formData.clear(),
						loading: true,
						refresh: !this.state.refresh,
					});
				}
				return Promise.resolve(formSubmissionDetails);
			})
			.catch((err) => {
				NotificationsService.error(
					"Something went wrong. Please try again later. If the issue persists contact your admin."
				);
				console.log(err);
			})
			.finally(() => {
				if (!flowToBeContinued) {
					this.setState({ submitting: false, loading: false });
				}
			});
	}

	renderSelectField(fieldConfig: ISelectFormField, fieldData: FormField, props) {
		console.log("renderSelectField:", fieldConfig, fieldData);
		return (
			<FormPicker
				{...props}
				field={fieldConfig}
				key={fieldConfig.id}
				value={fieldData.value}
				label={fieldConfig.label}
				onInputChange={this.onInputChange.bind(this)}
				invalid={fieldData.touched && !fieldData.valid}
				placeholder={fieldConfig.placeholder}
				// border
			/>
		);
	}

	renderCheckboxField(fieldConfig: ISelectFormField, fieldData: FormField, props) {
		console.log("renderCheckboxField: fieldConfig, fieldData:", fieldConfig, fieldData);
		return (
			<CheckboxList
				{...props}
				field={fieldConfig}
				fieldData={fieldData}
				key={fieldConfig.id}
				label={fieldConfig.label}
				values={fieldData.value}
				invalid={fieldData.touched && !fieldData.valid}
				onInputChange={this.onInputChange.bind(this)}
			/>
		);
	}

	renderFormField = (fieldConfig: IFormField) => {
		// const fieldConfig: IFormField = params.item as IFormField;
		// const fieldData: FormField = this.state.formData.fields[
		//   getFieldKey(fieldConfig)
		// ];
		const fieldKey: string = getFieldKey(fieldConfig);
		const fieldData: FormField = this.state.formData.fields[fieldKey];

		const getProps = (params): IFormInputProps => {
			const customProps: IFormInputCustomProps = {
				label: fieldConfig.label,
				field: fieldConfig,
				border: 1,
				onInputChange: this.onInputChange.bind(this),
				onBlur: this.onBlur.bind(this),
				invalid: !fieldData.valid && fieldData.touched,
				showIcon: fieldData.touched,
				showAsterisks: this.props.withAsterisks && fieldData.mandatory,
				asteriskTemplate: this.props.asteriskTemplate,
				isCorrect: fieldData.valid,
				enabled: !this.props.disableFields?.[fieldKey] && !fieldConfig.disabled,
				...this.props.customProps,
			};
			return {
				key: fieldConfig.id + ((!fieldData.valid && fieldData.touched) || false).toString(), // dana
				formField: fieldData,
				customProps,
				maxLength: fieldConfig.maxLength,
				autoCapitalize: "sentences",
				underlineColorAndroid: "transparent",
				defaultValue: this.state.formData.fields[getFieldKey(fieldConfig)].value,
				placeholder: fieldConfig.placeholder,
				...params,
			};
		};

		const props = getProps({ keyboardType: getKeyboardType(fieldConfig.fieldType) });
		console.debug("renderFormField: field, fieldData", fieldConfig, fieldData);
		console.debug("field props:", props);
		switch (fieldConfig.fieldType) {
			case "phone":
				if (this.context.renderFormInputPhone) {
					return this.context.renderFormInputPhone(props);
				}
				break;
			case "text":
			case "select":
			case "collection":
			case "yes/no":
			case "boolean":
			case "email":
				if (this.context.renderFormInputTextSelect) {
					return this.context.renderFormInputTextSelect(props);
				}
				return this.renderSelectField(fieldConfig as ISelectFormField, fieldData, props);
				break;
			case "checkbox":
				return this.renderCheckboxField(fieldConfig as ISelectFormField, fieldData, props);
			case "address": {
				if (this.context.renderFormInputAddress) {
					return this.context.renderFormInputAddress(props);
				}
			}
		}

		return <FormInput {...props} key={this.state.formData.fields[fieldKey].value} />;
	};

	renderSection = (section: ISection) => {
		const style = section.noBorder ? {} : [styles.form.group, styles.form.section];
		const sectionTxtStyle = {
			color: theme.colors.grey9,
		};
		return (
			<View nativeID={`laco-form-section-container-${section.id}`} style={style}>
				{!!section.name && (
					<Text
						nativeID={`laco-form-title-section-${section.id}`}
						style={[styles.text.pageTitle, sectionTxtStyle, { fontSize: getFontSize(20) }]}
						key={section.id}
					>
						{section.name}
					</Text>
				)}
				{!!section.description && (
					<DescriptionText
						nativeID={`laco-form-desc-section-${section.id}`}
						text={section.description}
						style={[styles.text.pageDescription, sectionTxtStyle]}
					/>
				)}
				<FlatList
					data={section.fields}
					keyExtractor={(field: IFormField) => field.id}
					// renderItem={item => this.renderFormField(item)}
					renderItem={
						(item: ListRenderItemInfo<IFormField>) => this.renderFormField(item.item) // this.state.formData.fields[getFieldKey(item.item)
					}
					extraData={this.state.refresh}
				/>
			</View>
		);
	};

	renderPrice() {
		const amount: number = this.state.formConfig.priceMultiplierField
			? this.state.formData.fields[this.state.formConfig.priceMultiplierField]?.value || 1
			: 1;
		const itemPrice: number = this.state.formConfig.productPrice.toFixed(2);
		const total: number = ((this.state.formConfig.productPrice as number) * amount).toFixed(2) as any as number;

		return (
			<Text style={{ marginTop: 10, marginBottom: 20, fontSize: 18 }}>
				Total ${itemPrice}{" "}
				{amount > 1 ? (
					<Text style={{ fontSize: 18 }}>
						X {amount} = {total}
					</Text>
				) : null}
			</Text>
		);
	}

	render() {
		if (this.state.loading) return <LoadingOverlay />;
		const form: IForm = this.state.formConfig;
		if (!form) return null;

		return (
			<View
				key={this.props.formId}
				style={[
					styles.layout.safeArea,
					{
						padding: dynamicSizeIncludingDesktop(20),
						paddingTop: dynamicSizeIncludingDesktop(5),
					},
				]}
			>
				<ScrollView>
					{!this.props.hideTitle && (
						<Text
							nativeID="laco-form-title"
							style={[styles.text.pageTitle, { marginBottom: dynamicSizeIncludingDesktop(10) }]}
						>
							{form.name}
						</Text>
					)}
					{!!form.description && (
						<DescriptionText
							nativeID="laco-form-desc"
							text={form.description}
							style={styles.text.pageDescription}
						/>
					)}
					{!!this.state.formData && (
						<>
							<FlatList
								data={form.fields}
								keyExtractor={(field: IFormField) => field.id}
								renderItem={
									(item: ListRenderItemInfo<IFormField>) => this.renderFormField(item.item) // this.state.formData.fields[getFieldKey(item.item)
								}
								extraData={this.state.refresh}
								// ItemSeparatorComponent={() => (
								//   <View style={{ height: dynamicSize(5) }} />
								// )}
							/>
							<FlatList
								data={form.sections}
								keyExtractor={(section: ISection) => section.id}
								// renderItem={item => this.renderSection(item)}
								renderItem={(item: ListRenderItemInfo<ISection>) =>
									this.renderSection(
										item.item
										// this.state.formData.fields[getFieldKey(item.item)]
									)
								}
								extraData={this.state.refresh}
							/>
						</>
					)}
					{!this.state.formConfig.hideSubmit && (
						<View style={{ marginTop: dynamicSize(10) }} nativeID="laco-form-submit-btn">
							{this.props.footerBtnContent}
							{this.state.formConfig.ecwidSku || this.state.formConfig.productPrice ? (
								<>
									{this.renderPrice()}
									<PMButton
										title="Submit and Go to Payment"
										disabled={this.state.submitting}
										onPress={() =>
											this.onSubmit(true).then((formSubmission: IFormSubmission) => {
												if (this.state.formConfig.ecwidSku && this.context.goToEcwidPayment) {
													this.context.goToEcwidPayment(
														this.state.formConfig.ecwidSku,
														this.state.formId,
														formSubmission.formSubmissionUserDetails?.email ||
															formSubmission["user-email"]
													);
												} else if (this.context.goToPayment) {
													this.context.goToPayment(formSubmission, this.state.formConfig);
												}
											})
										}
									/>
								</>
							) : (
								<PMButton
									title={
										this.props.btnText ||
										this.props.form?.btnText ||
										this.state.formConfig?.btnText ||
										"Submit"
									}
									disabled={this.state.submitting}
									onPress={() => this.onSubmit()}
								/>
							)}

							{this.state.submitting && <LoadingOverlay />}
						</View>
					)}
				</ScrollView>
			</View>
		);
	}
}

export default withFirebaseUtils(SubmitForm);
